import { useState } from "react";
import { siteData, ChangeLogEntry } from "@/store/siteData";

const sectionIcons: Record<string, string> = {
  yg_hero: "ri-home-4-line",
  yg_features: "ri-layout-grid-line",
  yg_cta: "ri-megaphone-line",
  yg_testimonials: "ri-chat-quote-line",
  yg_projects: "ri-building-2-line",
  yg_partners: "ri-links-line",
  yg_about: "ri-information-line",
};

const sectionColors: Record<string, { bg: string; text: string }> = {
  yg_hero:         { bg: "bg-violet-500/20",  text: "text-violet-300"  },
  yg_features:     { bg: "bg-sky-500/20",      text: "text-sky-300"     },
  yg_cta:          { bg: "bg-amber-500/20",    text: "text-amber-300"   },
  yg_testimonials: { bg: "bg-pink-500/20",     text: "text-pink-300"    },
  yg_projects:     { bg: "bg-emerald-500/20",  text: "text-emerald-300" },
  yg_partners:     { bg: "bg-orange-500/20",   text: "text-orange-300"  },
  yg_about:        { bg: "bg-[#3B82F6]/30",    text: "text-[#B8C5D8]"  },
};

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60)    return `${diff} saniye önce`;
    if (diff < 3600)  return `${Math.floor(diff / 60)} dakika önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    const days = Math.floor(diff / 86400);
    if (days < 7) return `${days} gün önce`;
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Özet diff: iki obje arasındaki string farklarını bul
function extractDiff(oldVal: unknown, newVal: unknown): Array<{ field: string; old: string; new: string }> {
  const diffs: Array<{ field: string; old: string; new: string }> = [];
  if (!oldVal || !newVal || typeof oldVal !== "object" || typeof newVal !== "object") return diffs;

  const flattenObj = (obj: unknown, prefix = ""): Record<string, string> => {
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      return { [prefix || "value"]: String(obj ?? "") };
    }
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        result[key] = String(v);
      } else if (typeof v === "object" && v !== null && !Array.isArray(v)) {
        Object.assign(result, flattenObj(v, key));
      }
    }
    return result;
  };

  const oldFlat = flattenObj(oldVal);
  const newFlat = flattenObj(newVal);

  const allKeys = new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)]);
  let count = 0;
  for (const key of allKeys) {
    if (count >= 5) break; // Max 5 diff göster
    const o = oldFlat[key] ?? "";
    const n = newFlat[key] ?? "";
    if (o !== n && o.length < 300 && n.length < 300) {
      diffs.push({ field: key, old: o, new: n });
      count++;
    }
  }
  return diffs;
}

// Diff panel (expandable)
function DiffPanel({ entry }: { entry: ChangeLogEntry }) {
  const [open, setOpen] = useState(false);
  const diffs = extractDiff(entry.oldValue, entry.newValue);
  if (diffs.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-1.5 text-xs text-[#3B82F6] hover:text-[#B8C5D8] transition-colors cursor-pointer"
      >
        <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
        {open ? "Değişimi Gizle" : `${diffs.length} alan değişti — Göster`}
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-2 border-l-2 border-[#2B3654]">
          {diffs.map((d, i) => (
            <div key={i} className="text-xs">
              <span className="text-[#7B8FAF] font-mono text-xs">{d.field}</span>
              <div className="flex gap-2 mt-1 flex-wrap">
                {d.old && (
                  <div className="flex items-start gap-1 bg-red-500/10 border border-red-500/20 rounded px-2 py-1 max-w-xs">
                    <span className="text-red-400 font-bold flex-shrink-0">−</span>
                    <span className="text-red-300 break-all line-clamp-2">{d.old}</span>
                  </div>
                )}
                {d.new && (
                  <div className="flex items-start gap-1 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1 max-w-xs">
                    <span className="text-emerald-400 font-bold flex-shrink-0">+</span>
                    <span className="text-emerald-300 break-all line-clamp-2">{d.new}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const sectionFilterOptions = [
  { key: "all", label: "Tümü" },
  { key: "yg_hero", label: "Hero" },
  { key: "yg_features", label: "Özellikler" },
  { key: "yg_cta", label: "CTA" },
  { key: "yg_testimonials", label: "Referanslar" },
  { key: "yg_projects", label: "Projeler" },
  { key: "yg_partners", label: "Ortaklar" },
  { key: "yg_about", label: "Hakkımızda" },
];

function StatsRow({ entries }: { entries: ChangeLogEntry[] }) {
  const today = entries.filter((e) => {
    return new Date(e.timestamp).toDateString() === new Date().toDateString();
  }).length;
  const sections = new Set(entries.map((e) => e.sectionKey)).size;
  const users = new Set(entries.map((e) => e.user).filter(Boolean)).size;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
        <p className="text-white font-black text-2xl">{entries.length}</p>
        <p className="text-[#7B8FAF] text-xs mt-1">Toplam Değişiklik</p>
      </div>
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
        <p className="text-white font-black text-2xl">{today}</p>
        <p className="text-[#7B8FAF] text-xs mt-1">Bugün</p>
      </div>
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
        <p className="text-white font-black text-2xl">{sections}</p>
        <p className="text-[#7B8FAF] text-xs mt-1">Farklı Bölüm</p>
      </div>
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4">
        <p className="text-white font-black text-2xl">{users}</p>
        <p className="text-[#7B8FAF] text-xs mt-1">Aktif Kullanıcı</p>
      </div>
    </div>
  );
}

export default function ChangeLog() {
  const [entries, setEntries] = useState<ChangeLogEntry[]>(siteData.getChangeLog());
  const [filter, setFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");
  const [confirmClear, setConfirmClear] = useState(false);

  const handleClear = () => {
    if (confirmClear) {
      siteData.clearChangeLog();
      setEntries([]);
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
    }
  };

  const uniqueUsers = ["all", ...Array.from(new Set(entries.map((e) => e.user).filter((u): u is string => !!u)))];

  const filtered = entries.filter((e) => {
    const matchSection = filter === "all" || e.sectionKey === filter;
    const matchUser = userFilter === "all" || e.user === userFilter;
    return matchSection && matchUser;
  });

  const col = (key: string) => sectionColors[key] || { bg: "bg-[#1E2B4A]", text: "text-[#7B8FAF]" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Değişiklik Günlüğü</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">Kim, ne zaman, hangi veriyi, nasıl değiştirdi</p>
        </div>
        <button
          onClick={handleClear}
          className={`px-4 py-2 text-sm rounded-lg border transition-all cursor-pointer whitespace-nowrap ${confirmClear ? "border-red-500 text-red-400 bg-red-500/10" : "border-[#2B3654] text-[#7B8FAF] hover:border-red-500/50 hover:text-red-400"}`}
        >
          {confirmClear ? "Emin misin? Tekrar tıkla" : "Günlüğü Temizle"}
        </button>
      </div>

      <StatsRow entries={entries} />

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex gap-2 flex-wrap">
          {sectionFilterOptions.map((opt) => (
            <button key={opt.key} onClick={() => setFilter(opt.key)}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all cursor-pointer whitespace-nowrap ${filter === opt.key ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "border-[#2B3654] text-[#7B8FAF] hover:border-[#3B82F6] hover:text-[#B8C5D8]"}`}>
              {opt.label}
            </button>
          ))}
        </div>
        {uniqueUsers.length > 2 && (
          <div className="flex gap-2 flex-wrap">
            <span className="text-[#465886] text-xs self-center">Kullanıcı:</span>
            {uniqueUsers.map((u) => (
              <button key={u} onClick={() => setUserFilter(u)}
                className={`px-3 py-1 text-xs rounded-full border transition-all cursor-pointer whitespace-nowrap ${userFilter === u ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "border-[#2B3654] text-[#7B8FAF] hover:text-amber-300"}`}>
                {u === "all" ? "Hepsi" : u}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-[#0F1729] rounded-xl mb-4">
            <i className="ri-history-line text-3xl text-[#3B82F6]"></i>
          </div>
          <p className="text-white font-medium">Henüz değişiklik yok</p>
          <p className="text-[#7B8FAF] text-sm mt-1">Admin panelinden bir şey kaydettiğinde burada görünür</p>
        </div>
      ) : (
        <div className="bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden">
          <div className="divide-y divide-[#1E2B4A]">
            {filtered.map((entry, i) => (
              <div key={entry.id}
                className={`flex items-start gap-4 px-6 py-4 hover:bg-white/[0.02] transition-colors ${i === 0 ? "border-l-2 border-l-[#3B82F6]" : ""}`}>
                <div className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg mt-0.5 ${col(entry.sectionKey).bg} ${col(entry.sectionKey).text}`}>
                  <i className={`${sectionIcons[entry.sectionKey] || "ri-edit-line"} text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${col(entry.sectionKey).bg} ${col(entry.sectionKey).text}`}>
                      {entry.section}
                    </span>
                    <span className="text-[#7B8FAF] text-xs font-medium">{entry.action}</span>
                    {entry.user && (
                      <span className="flex items-center gap-1 text-xs text-amber-400/80">
                        <i className="ri-user-line text-xs"></i>
                        {entry.user}
                      </span>
                    )}
                  </div>
                  {entry.details && <p className="text-[#5E7398] text-xs truncate">{entry.details}</p>}
                  <DiffPanel entry={entry} />
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-[#7B8FAF] text-xs whitespace-nowrap">{formatTime(entry.timestamp)}</p>
                  <p className="text-[#465886] text-xs mt-0.5 whitespace-nowrap hidden sm:block">{formatDateTime(entry.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-[#0F1729] border border-[#1E2B4A] rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-lg flex-shrink-0 mt-0.5">
          <i className="ri-information-line text-[#3B82F6]"></i>
        </div>
        <p className="text-[#7B8FAF] text-xs leading-relaxed">
          Günlük veritabanında saklanır. Son <strong className="text-[#B8C5D8]">200 değişiklik</strong> tutulur.
          Her satırda <strong className="text-[#B8C5D8]">kimin, ne zaman, hangi alanı nasıl değiştirdiği</strong> görünür.
          &quot;Değişimi Göster&quot; butonuyla Eski → Yeni karşılaştırması yapabilirsin.
        </p>
      </div>
    </div>
  );
}
